import React, { useState, useEffect } from 'react';
import { FileText, Save, CheckCircle, AlertTriangle, Users, Building2, Briefcase, RefreshCw, Eye } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const PolicyManagement = () => {
  const [activeTab, setActiveTab] = useState('host');
  const [policies, setPolicies] = useState({
    host_policy_text: '',
    agency_policy_text: '',
    bd_policy_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: 'success' }), 3500);
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/system/settings`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        const d = response.data.data;
        setPolicies({
          host_policy_text: d.host_policy_text || '',
          agency_policy_text: d.agency_policy_text || '',
          bd_policy_text: d.bd_policy_text || ''
        });
      }
    } catch (err) {
      console.error(err);
      showToast('Failed to load platform policies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async (key) => {
    setSaving(true);
    try {
      const value = policies[key] || '';
      const res = await axios.put(
        `${constant.API_BASE_URL}/admin/business/system/settings`,
        { key, value },
        { headers: { Authorization: sessionStorage.getItem('adminToken') } }
      );
      if (res.data.success) {
        showToast('Policy updated successfully! Changes reflect live in the mobile app.');
      }
    } catch (err) {
      console.error(err);
      showToast('Error saving policy text', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, { key: 'host_policy_text', value: policies.host_policy_text || '' }, { headers: { Authorization: sessionStorage.getItem('adminToken') } }),
        axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, { key: 'agency_policy_text', value: policies.agency_policy_text || '' }, { headers: { Authorization: sessionStorage.getItem('adminToken') } }),
        axios.put(`${constant.API_BASE_URL}/admin/business/system/settings`, { key: 'bd_policy_text', value: policies.bd_policy_text || '' }, { headers: { Authorization: sessionStorage.getItem('adminToken') } })
      ]);
      showToast('All platform policies saved successfully!');
    } catch (err) {
      console.error(err);
      showToast('Failed to save some policies', 'error');
    } finally {
      setSaving(false);
    }
  };

  const tabConfig = {
    host: {
      title: 'Host Policy & Guidelines',
      key: 'host_policy_text',
      color: 'purple',
      icon: Users,
      description: 'Official rules for hosts, live stream requirements, coin targets, and daily active hour rules.',
      placeholder: 'Enter official host rules, live streaming hours, and target reward policy details...\nExample:\n1. Hosts must stream at least 60 minutes for a valid day.\n2. Complete 10 active streaming days per month for full payout.\n3. Gifts received on mic contribute 100% to monthly target progress.'
    },
    agency: {
      title: 'Agency Commission Policy',
      key: 'agency_policy_text',
      color: 'pink',
      icon: Building2,
      description: 'Rules for agency owners, commission percentages, host recruiting guidelines, and payout terms.',
      placeholder: 'Enter agency commission structures, payout cycles, and guidelines...\nExample:\n1. Agency commissions are calculated on hosts completing >= Level 1 target.\n2. Commission shares range from 4% up to 22% based on total agency tier.\n3. Monthly settlements are released within the first 5 business days.'
    },
    bd: {
      title: 'BD (Business Developer) Policy',
      key: 'bd_policy_text',
      color: 'amber',
      icon: Briefcase,
      description: 'Terms for Business Developers, recruitment quotas, agency onboarding incentives, and manager cuts.',
      placeholder: 'Enter business developer monthly targets, commission cuts, and agency onboarding rules...\nExample:\n1. BD earns 3% - 6% override commission on all onboarded agencies.\n2. Minimum 3 active new agencies required per calendar month.\n3. Inactive agencies for > 60 days may be reallocated.'
    }
  };

  const currentTab = tabConfig[activeTab];

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800 flex items-center gap-3">
            <FileText className="text-purple-600" size={32} /> Platform Policy Management
          </h2>
          <p className="text-slate-500 mt-1">
            Manage official Host Policy, Agency Policy, and BD Manager Guidelines displayed dynamically inside the mobile app.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPolicies}
            disabled={loading}
            className="flex items-center gap-2 bg-white text-slate-700 px-4 py-2.5 rounded-xl font-semibold shadow-sm border border-slate-200 hover:bg-slate-50 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-purple-500/25 hover:opacity-95 transition"
          >
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />} Save All Policies
          </button>
        </div>
      </div>

      {/* Policy Selector Tabs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(tabConfig).map(([key, config]) => {
          const Icon = config.icon;
          const isSelected = activeTab === key;
          const hasText = !!policies[config.key]?.trim();

          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`p-5 rounded-2xl border text-left transition-all relative overflow-hidden ${
                isSelected
                  ? 'bg-white border-purple-500 shadow-xl ring-2 ring-purple-500/20'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-sm'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl ${isSelected ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'}`}>
                  <Icon size={20} />
                </div>
                <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${
                  hasText ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'
                }`}>
                  {hasText ? '✓ Configured' : 'Empty'}
                </span>
              </div>
              <h4 className="font-extrabold text-slate-800 text-base">{config.title}</h4>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{config.description}</p>
            </button>
          );
        })}
      </div>

      {/* Editor & Preview Split */}
      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-xl p-20 text-center">
          <div className="animate-spin w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading platform policies...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Editor Panel */}
          <div className="lg:col-span-7 bg-white p-6 md:p-8 rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-black text-slate-800 flex items-center gap-2">
                  <currentTab.icon size={20} className="text-purple-600" />
                  {currentTab.title} Text
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">Supports line breaks, bullet points, and numbered lists</p>
              </div>
              <button
                type="button"
                onClick={() => handleSavePolicy(currentTab.key)}
                disabled={saving}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-purple-500/20 transition disabled:opacity-50"
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save {activeTab.toUpperCase()} Policy
              </button>
            </div>

            <textarea
              rows={14}
              placeholder={currentTab.placeholder}
              value={policies[currentTab.key] || ''}
              onChange={(e) => setPolicies({ ...policies, [currentTab.key]: e.target.value })}
              className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-purple-500/20 text-sm font-medium text-slate-800 leading-relaxed transition"
            />

            <div className="flex items-center justify-between text-xs text-slate-400 pt-2">
              <span>{policies[currentTab.key]?.length || 0} characters</span>
              <span className="text-purple-600 font-semibold">Changes sync immediately to app screens</span>
            </div>
          </div>

          {/* Live Mobile App Preview */}
          <div className="lg:col-span-5 bg-gradient-to-b from-[#0C071E] to-[#19102E] p-6 rounded-3xl border border-purple-900/30 text-white shadow-2xl flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-white/10">
                <div className="flex items-center gap-2 text-xs font-bold text-purple-300">
                  <Eye size={14} /> App Screen Preview
                </div>
                <span className="text-[10px] font-black uppercase bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                  Live View
                </span>
              </div>

              {/* Simulated App Card */}
              <div className="bg-gradient-to-r from-purple-700 to-indigo-700 p-4 rounded-2xl shadow-lg shadow-purple-900/50">
                <span className="text-[10px] font-black uppercase text-amber-300 tracking-wider">OFFICIAL POLICY</span>
                <h4 className="text-base font-extrabold text-white mt-1">{currentTab.title.toUpperCase()}</h4>
                <p className="text-xs text-purple-200 mt-0.5">XOXO LIVE PLATFORM GUIDELINES</p>
              </div>

              {/* Policy Body */}
              <div className="bg-white/5 border border-white/10 p-5 rounded-2xl max-h-[380px] overflow-y-auto scrollbar-thin space-y-2">
                <div className="text-xs font-bold text-purple-300 uppercase tracking-wider mb-2">Policy Details:</div>
                {policies[currentTab.key]?.trim() ? (
                  <div className="text-xs text-white/80 whitespace-pre-wrap leading-relaxed">
                    {policies[currentTab.key]}
                  </div>
                ) : (
                  <div className="text-xs text-white/40 italic">
                    No custom policy text entered yet. The default platform rules will be shown in the app.
                  </div>
                )}
              </div>
            </div>

            <div className="pt-4 text-center border-t border-white/10 text-[11px] text-white/50">
              Hosts & agencies see this text inside the App Policy view.
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default PolicyManagement;
